<template>
  <div class="tier-blocks">
    <div class="tier-blocks__header">
      <div class="header-controls">
        <label class="header-label">Проект:</label>
        <input
          v-model="residentSearchQuery"
          type="text"
          placeholder="Поиск проекта..."
          class="simple-input"
          @input="onSearchChange"
        />
        <label class="filter-label">
          <input
            type="checkbox"
            v-model="filterOnlyWithBlocks"
            @change="onFilterChange"
            class="filter-checkbox"
          />
          <span>Только с блоками</span>
        </label>
        <q-space />
        <q-btn
          icon="admin_panel_settings"
          label="Админ"
          color="deep-purple"
          size="sm"
          dense
          @click="onAdminClick"
        >
          <q-tooltip>Административные операции</q-tooltip>
        </q-btn>
      </div>
    </div>

    <div class="tier-blocks__content">
      <!-- Колонка 1: Проект -->
      <div class="tier-blocks__column tier-blocks__column--residents">
        <div class="column-header">
          <q-icon name="apartment" size="14px" />
          <span class="column-title">Проект</span>
          <q-badge v-if="residents.length" :label="filteredResidents.length + '/' + residents.length" color="grey-4" text-color="grey-8" />
        </div>
        
        <div v-if="!isLoadingResidents && filteredResidents.length > 0" class="column-list-container">
          <q-virtual-scroll
            :items="filteredResidents"
            :virtual-scroll-item-size="36"
            :virtual-scroll-sticky-size-start="0"
            :virtual-scroll-sticky-size-end="0"
            class="virtual-scroll-list"
          >
            <template v-slot:default="{ item: resident, index }">
              <q-item
                :key="resident.resident_id"
                clickable
                v-ripple
                :active="selectedResident?.resident_id === resident.resident_id"
                @click="selectResident(resident)"
                class="list-item"
                dense
              >
                <q-item-section>
                  <q-item-label class="item-label">{{ resident.resident_name }}</q-item-label>
                  <q-item-label v-if="showDebugIds" caption class="debug-id-visible">resident_id: {{ resident.resident_id }}</q-item-label>
                </q-item-section>
                <q-item-section side v-if="resident.has_blocks_">
                  <q-icon name="check_circle" color="positive" size="16px" />
                </q-item-section>
              </q-item>
            </template>
          </q-virtual-scroll>
        </div>
        
        <div v-else-if="isLoadingResidents" class="loading-state">
          <q-spinner color="primary" size="24px" />
        </div>
        
        <div v-else class="empty-state">
          <p>Нет проектов</p>
        </div>
      </div>

      <!-- Колонка 2: Группы работ -->
      <div class="tier-blocks__column tier-blocks__column--with-blocks tier-blocks__column--work-groups">
        <!-- Блоки над группами работ -->
        <div v-if="selectedResident" class="blocks-header">
          <q-btn
            icon="add"
            flat
            dense
            round
            size="xs"
            color="primary"
            @click="onAddBlockClick"
            class="add-block-btn-inline"
          >
            <q-tooltip>Добавить блок</q-tooltip>
          </q-btn>
          
          <div class="blocks-wrap-container">
            <div
              v-for="block in blocks"
              :key="block.block_id"
              @click="selectBlock(block)"
              :class="['block-item', { 'block-item--active': selectedBlock?.block_id === block.block_id }]"
            >
              <span class="block-item__name">{{ block.block_name }}</span>
              <span v-if="showDebugIds" class="debug-id-visible-inline">id:{{ block.block_id }}</span>
              <q-icon 
                v-if="block.has_work_groups_" 
                name="check_circle" 
                color="positive" 
                size="14px" 
                class="block-item__icon"
              />
              <div class="block-item__actions" @click.stop>
                <q-btn
                  icon="people"
                  flat
                  dense
                  round
                  size="xs"
                  color="secondary"
                  @click="onBlockEmployeesClick(block)"
                >
                  <q-tooltip>Пользователи блока</q-tooltip>
                </q-btn>
                <q-btn
                  icon="edit"
                  flat
                  dense
                  round
                  size="xs"
                  color="primary"
                  @click="onEditBlockClick(block)"
                >
                  <q-tooltip>Редактировать</q-tooltip>
                </q-btn>
                <q-btn
                  icon="delete"
                  flat
                  dense
                  round
                  size="xs"
                  color="negative"
                  @click="onDeleteBlockClick(block)"
                >
                  <q-tooltip>Удалить</q-tooltip>
                </q-btn>
              </div>
            </div>
          </div>
        </div>
        
        <div class="column-header">
          <q-icon name="construction" size="14px" />
          <span class="column-title">Группы работ</span>
          <q-badge v-if="workGroups.length" :label="assignedWorkGroupsCount + '/' + workGroups.length" color="grey-4" text-color="grey-8" />
        </div>
        
        <div v-if="!selectedBlock" class="empty-state">
          <q-icon name="arrow_back" color="grey-5" size="20px" />
          <p>← Выберите блок</p>
        </div>
        
        <div v-else-if="!isLoadingWorkGroups && workGroups.length > 0" class="column-list-container">
          <q-list dense class="column-list">
            <q-item
              v-for="workGroup in workGroups"
              :key="workGroup.work_group_dict_id"
              class="list-item work-group-item"
              dense
            >
              <q-item-section side style="min-width: 20px; padding-right: 4px;">
                <q-checkbox
                  :model-value="workGroup.is_assigned_"
                  @update:model-value="(val) => onWorkGroupCheckboxChange(val, workGroup)"
                  @click.stop
                  size="xs"
                  dense
                />
              </q-item-section>
              <q-item-section 
                @click="workGroup.is_assigned_ ? selectWorkGroup(workGroup) : null"
                :class="{ 'clickable-section': workGroup.is_assigned_, 'active-section': selectedWorkGroup?.work_group_id === workGroup.work_group_id }"
              >
                <q-item-label class="item-label work-group-name">{{ workGroup.work_group_name }}</q-item-label>
                <q-item-label v-if="showDebugIds" caption class="debug-id-visible">
                  <template v-if="workGroup.is_assigned_">work_group_id: {{ workGroup.work_group_id }}</template>
                  <template v-else>work_group_dict_id: {{ workGroup.work_group_dict_id }}</template>
                </q-item-label>
                <q-item-label v-if="workGroup.is_assigned_ && workGroup.date_start" caption class="item-caption work-group-dates">
                  <q-icon name="event" size="11px" style="margin-right: 4px;" />
                  {{ formatDate(workGroup.date_start) }} - {{ formatDate(workGroup.date_end) || '...' }}
                </q-item-label>
                <q-item-label v-if="workGroup.is_assigned_ && workGroup.employee_fio" caption class="item-caption work-group-employee">
                  <q-icon name="person" size="11px" style="margin-right: 4px;" />
                  {{ workGroup.employee_fio }}
                </q-item-label>
              </q-item-section>
              <q-item-section side v-if="workGroup.is_assigned_">
                <q-btn
                  icon="visibility"
                  flat
                  dense
                  round
                  size="xs"
                  color="primary"
                  @click="onViewTasksClick(workGroup)"
                  style="margin-right: 2px;"
                >
                  <q-tooltip>Просмотр задач</q-tooltip>
                </q-btn>
                <q-btn
                  icon="edit"
                  flat
                  dense
                  round
                  size="xs"
                  color="primary"
                  @click="onEditWorkGroupClick(workGroup)"
                >
                  <q-tooltip>Редактировать</q-tooltip>
                </q-btn>
              </q-item-section>
            </q-item>
          </q-list>
        </div>
        
        <div v-else-if="isLoadingWorkGroups" class="loading-state">
          <q-spinner color="primary" size="24px" />
        </div>
        
        <div v-else class="empty-state">
          <p>Нет групп работ</p>
        </div>
      </div>

      <!-- Колонка 3: Этажи выбранной группы работ -->
      <div class="tier-blocks__column tier-blocks__column--levels">
        <div class="column-header">
          <q-icon name="layers" size="14px" />
          <span class="column-title">Этажи</span>
          <q-badge v-if="levels.length" :label="levels.length" color="grey-4" text-color="grey-8" />
          <q-checkbox
            v-if="selectedWorkGroup && selectedWorkGroup.is_level_need && levels.length > 0"
            size="xs"
            dense
            :model-value="isAllLevelsSelected"
            @update:model-value="toggleSelectAllLevels"
            class="q-ml-sm"
          >
            <q-tooltip>Выделить все этажи</q-tooltip>
          </q-checkbox>
          <q-btn
            v-if="selectedWorkGroup && selectedWorkGroup.is_level_need"
            icon="add"
            flat
            dense
            round
            size="xs"
            color="primary"
            @click="onAddLevelClick"
          >
            <q-tooltip>Добавить этажи</q-tooltip>
          </q-btn>
          <q-btn
            v-if="selectedWorkGroup && selectedWorkGroup.is_level_need && selectedLevelIds.length > 0"
            icon="delete_sweep"
            flat
            dense
            round
            size="xs"
            color="negative"
            class="q-ml-xs"
            @click="onBulkDeleteLevelsClick"
          >
            <q-tooltip>Удалить выбранные этажи</q-tooltip>
          </q-btn>
        </div>
        
        <div v-if="!selectedWorkGroup" class="empty-state">
          <q-icon name="arrow_back" color="grey-5" size="20px" />
          <p>← Выберите группу работ</p>
        </div>

        <div v-else-if="selectedWorkGroup && !selectedWorkGroup.is_level_need" class="info-state">
          <q-icon name="info" color="blue-5" size="32px" />
          <p class="info-message">Для данной группы работ не требуется заполнение этажей</p>
        </div>
        
        <template v-else>
          <div v-if="!isLoadingLevels && levels.length > 0" class="column-list-container">
            <q-list dense class="column-list">
              <q-item
                v-for="level in levels"
                :key="level.work_group_level_id"
                class="list-item level-item"
                dense
              >
                <q-item-section side style="min-width: 30px; padding-right: 8px;">
                  <q-checkbox
                    :model-value="selectedLevelIds.includes(level.work_group_level_id)"
                    @update:model-value="(val) => onLevelCheckboxChange(val, level)"
                    size="xs"
                    dense
                  />
                  <span class="floor-order-badge q-ml-xs">{{ level.floor_order }}</span>
                </q-item-section>
                <q-item-section>
                  <q-item-label class="item-label">Этаж {{ level.level_num }}</q-item-label>
                  <q-item-label v-if="showDebugIds" caption class="debug-id-visible">work_group_level_id: {{ level.work_group_level_id }}</q-item-label>
                  <q-item-label v-if="level.level_date_start" caption class="item-caption">
                    {{ formatDate(level.level_date_start) }} - {{ formatDate(level.level_date_end) || '...' }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-btn
                    icon="edit"
                    flat
                    dense
                    round
                    size="xs"
                    color="primary"
                    @click="onEditLevelClick(level)"
                    style="margin-right: 2px;"
                  >
                    <q-tooltip>Редактировать</q-tooltip>
                  </q-btn>
                  <q-btn
                    icon="delete"
                    flat
                    dense
                    round
                    size="xs"
                    color="negative"
                    @click="onDeleteLevelClick(level)"
                  >
                    <q-tooltip>Удалить</q-tooltip>
                  </q-btn>
                </q-item-section>
              </q-item>
            </q-list>
          </div>
          
          <div v-else-if="isLoadingLevels" class="loading-state">
            <q-spinner color="primary" size="24px" />
          </div>
          
          <div v-else class="empty-state">
            <p>Нет этажей</p>
          </div>
        </template>
      </div>
    </div>

    <!-- Диалог подтверждения -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 400px; max-width: 500px;">
        <q-card-section class="bg-negative text-white">
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>

        <q-card-section style="white-space: pre-wrap;">
          {{ confirmDialog.message }}
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог пользователей блока -->
    <q-dialog v-model="employeesDialog.show" persistent>
      <q-card style="min-width: 500px; max-width: 700px;">
        <q-card-section class="bg-primary text-white">
          <div class="text-h6">
            <q-icon name="people" class="q-mr-sm" />
            Пользователи блока: {{ employeesDialog.block?.block_name }}
          </div>
        </q-card-section>

        <q-card-section>
          <div class="row items-center q-mb-md">
            <q-btn
              icon="add"
              label="Добавить"
              color="primary"
              size="sm"
              @click="onAddBlockEmployeeClick"
              class="q-mr-sm"
            />
            <q-btn
              v-if="blockEmployees.length > 0"
              icon="content_copy"
              label="Копировать на другие блоки"
              color="secondary"
              size="sm"
              outline
              @click="onCopyEmployeesClick"
            />
          </div>
          
          <div v-if="isLoadingBlockEmployees" class="text-center q-pa-md">
            <q-spinner color="primary" size="32px" />
          </div>
          
          <q-list v-else-if="blockEmployees.length > 0" dense bordered separator style="max-height: 400px; overflow-y: auto;">
            <q-item v-for="emp in blockEmployees" :key="emp.block_employee_id">
              <q-item-section avatar>
                <q-avatar color="primary" text-color="white" size="32px">
                  {{ emp.employee_fio?.charAt(0) || '?' }}
                </q-avatar>
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ emp.employee_fio }}</q-item-label>
                <q-item-label caption>ID: {{ emp.employee_id }} | {{ emp.employee_login }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-btn
                  icon="delete"
                  flat
                  dense
                  round
                  size="sm"
                  color="negative"
                  @click="onDeleteBlockEmployeeClick(emp)"
                >
                  <q-tooltip>Удалить</q-tooltip>
                </q-btn>
              </q-item-section>
            </q-item>
          </q-list>
          
          <div v-else class="text-center text-grey q-pa-lg">
            <q-icon name="people_outline" size="48px" class="q-mb-sm" />
            <div>Нет пользователей</div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Закрыть" color="grey" @click="employeesDialog.show = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог копирования пользователей -->
    <q-dialog v-model="copyDialog.show" persistent>
      <q-card style="min-width: 450px; max-width: 600px;">
        <q-card-section class="bg-primary text-white">
          <div class="text-h6">Копировать пользователей</div>
        </q-card-section>

        <q-card-section>
          <p class="q-mb-md">Выберите блоки, на которые нужно скопировать пользователей из блока <b>{{ employeesDialog.block?.block_name }}</b>:</p>
          
          <q-list dense bordered separator style="max-height: 300px; overflow-y: auto;">
            <q-item 
              v-for="block in blocksForCopyFromDialog" 
              :key="block.block_id"
              tag="label"
            >
              <q-item-section avatar>
                <q-checkbox v-model="copyDialog.selectedBlocks" :val="block.block_id" />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ block.block_name }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="copyDialog.show = false" />
          <q-btn 
            unelevated 
            label="Копировать" 
            color="primary" 
            :disable="copyDialog.selectedBlocks.length === 0"
            @click="onConfirmCopyEmployees" 
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог добавления пользователя -->
    <q-dialog v-model="addEmployeeDialog.show" persistent>
      <q-card style="min-width: 400px;">
        <q-card-section class="bg-primary text-white">
          <div class="text-h6">Добавить пользователя</div>
        </q-card-section>

        <q-card-section>
          <q-select
            v-model="addEmployeeDialog.selectedEmployeeId"
            :options="filteredEmployeesForAdd"
            option-value="employee_id"
            option-label="fio"
            emit-value
            map-options
            label="Выберите пользователя"
            outlined
            dense
            :loading="isLoadingAllEmployees"
            use-input
            input-debounce="300"
            @filter="filterEmployees"
          >
            <template v-slot:option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section>
                  <q-item-label>{{ getEmployeeName(scope.opt.fio) }}</q-item-label>
                  <q-item-label caption>ID: {{ scope.opt.employee_id }} | {{ scope.opt.email }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
            <template v-slot:no-option>
              <q-item>
                <q-item-section class="text-grey">Пользователь не найден</q-item-section>
              </q-item>
            </template>
          </q-select>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="addEmployeeDialog.show = false" />
          <q-btn 
            unelevated 
            label="Добавить" 
            color="primary" 
            :disable="!addEmployeeDialog.selectedEmployeeId"
            @click="onConfirmAddEmployee" 
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог ввода пин-кода -->
    <q-dialog v-model="pinDialog.show" persistent>
      <q-card style="min-width: 300px;">
        <q-card-section class="bg-deep-purple text-white">
          <div class="text-h6">
            <q-icon name="lock" class="q-mr-sm" />
            Введите PIN-код
          </div>
        </q-card-section>

        <q-card-section>
          <q-input
            v-model="pinDialog.pin"
            type="password"
            label="Код доступа"
            outlined
            dense
            maxlength="4"
            :error="pinDialog.error"
            error-message="Неверный код"
            @update:model-value="onPinInput"
            autofocus
            autocomplete="new-password"
            name="tier-admin-pin-input"
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="pinDialog.show = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог админ операций -->
    <q-dialog v-model="adminDialog.show">
      <q-card style="min-width: 400px;">
        <q-card-section class="bg-deep-purple text-white">
          <div class="text-h6">
            <q-icon name="admin_panel_settings" class="q-mr-sm" />
            Административные операции
          </div>
        </q-card-section>

        <q-card-section class="q-gutter-sm">
          <div class="row q-gutter-sm">
            <q-btn
              icon="refresh"
              label="Пересчёт всего"
              color="orange"
              :loading="isRecalculating"
              @click="onFullRecalcClick"
            >
              <q-tooltip>Полный пересчёт долгов и дней отставания по всем проектам</q-tooltip>
            </q-btn>
            
            <q-btn
              icon="warning"
              label="Аномалии"
              color="red"
              @click="onViewAnomaliesClick"
            >
              <q-tooltip>Показать нарушения целостности план/факт</q-tooltip>
            </q-btn>
          </div>
          
          <q-separator class="q-my-sm" />
          
          <q-toggle
            v-model="showDebugIds"
            label="Показать ID"
            color="orange"
            icon="bug_report"
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Закрыть" color="grey" @click="adminDialog.show = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог аномалий -->
    <q-dialog v-model="anomaliesDialog.show" maximized>
      <q-card>
        <q-card-section class="bg-red text-white">
          <div class="row items-center">
            <div class="text-h6">
              <q-icon name="warning" class="q-mr-sm" />
              Аномалии целостности план/факт
            </div>
            <q-space />
            <q-btn icon="close" flat round dense @click="anomaliesDialog.show = false" />
          </div>
        </q-card-section>

        <q-card-section>
          <div v-if="isLoadingAnomalies" class="text-center q-pa-lg">
            <q-spinner color="primary" size="48px" />
            <div class="q-mt-md">Загрузка данных...</div>
          </div>
          
          <div v-else-if="anomaliesData.length === 0" class="text-center q-pa-lg text-positive">
            <q-icon name="check_circle" size="64px" />
            <div class="text-h6 q-mt-md">Аномалий не обнаружено!</div>
            <div class="text-grey">Все данные в порядке</div>
          </div>
          
          <q-table
            v-else
            :rows="anomaliesData"
            :columns="anomaliesColumns"
            row-key="work_group_id"
            dense
            flat
            bordered
            :pagination="{ rowsPerPage: 50 }"
            class="anomalies-table"
          >
            <template v-slot:body-cell-error_message="props">
              <q-td :props="props" class="text-negative">
                {{ props.value }}
              </q-td>
            </template>
          </q-table>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useAppStore } from "@/services/store/app";
import {
  getResidents,
  getBlocksList,
  modifyBlock,
  deleteBlock,
  getWorkGroupsList,
  modifyWorkGroup,
  deleteWorkGroup,
  getWorkGroupLevels,
  modifyWorkGroupLevel,
  deleteWorkGroupLevel,
  bulkAddWorkGroupLevels,
  getBlockEmployees,
  addBlockEmployee,
  deleteBlockEmployee,
  copyBlockEmployees,
  getEmployees,
  fullRecalcAll,
  getWorkGroupDeleteStats,
  getPlanFactAnomalies
} from "@/views/TierBlocks/services";
import { MODAL_NAMES } from "@/constants";
import { MODAL_NAMES_AIDAR } from "@/constants/index_aidar";
import { formatDate } from '@/utils/formatDate';

export default {
  name: "TierBlocks",
  data() {
    return {
      residents: [],
      blocks: [],
      workGroups: [],
      levels: [],
      selectedResident: null,
      selectedBlock: null,
      selectedWorkGroup: null,
      isLoadingResidents: false,
      isLoadingBlocks: false,
      isLoadingWorkGroups: false,
      isLoadingLevels: false,
      residentSearchQuery: '',
      filterOnlyWithBlocks: false,
      showDebugIds: false,
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
      // Данные для пользователей блока
      blockEmployees: [],
      isLoadingBlockEmployees: false,
      allEmployees: [],
      isLoadingAllEmployees: false,
      // Диалог пользователей блока
      employeesDialog: {
        show: false,
        block: null
      },
      // Диалог копирования пользователей
      copyDialog: {
        show: false,
        selectedBlocks: []
      },
      // Диалог добавления пользователя
      addEmployeeDialog: {
        show: false,
        selectedEmployeeId: null
      },
      employeeSearchQuery: '',
      isRecalculating: false,
      // Админ модалка
      pinDialog: {
        show: false,
        pin: '',
        error: false
      },
      adminDialog: {
        show: false
      },
      anomaliesDialog: {
        show: false
      },
      anomaliesData: [],
      isLoadingAnomalies: false,
      selectedLevelIds: [],
    };
  },
  computed: {
    filteredResidents() {
      let filtered = this.residents;
      
      // Фильтр по наличию блоков
      if (this.filterOnlyWithBlocks) {
        filtered = filtered.filter(r => r.has_blocks_);
      }
      
      // Поиск по названию
      if (this.residentSearchQuery) {
        const query = this.residentSearchQuery.toLowerCase();
        filtered = filtered.filter(r => 
          r.resident_name.toLowerCase().includes(query)
        );
      }
      
      return filtered;
    },
    anomaliesColumns() {
      return [
        { name: 'resident_name', label: 'ЖК', field: 'resident_name', align: 'left', sortable: true },
        { name: 'block_name', label: 'Блок', field: 'block_name', align: 'left', sortable: true },
        { name: 'work_group_name', label: 'Группа работ', field: 'work_group_name', align: 'left', sortable: true },
        { name: 'task_name', label: 'Задача', field: 'task_name', align: 'left', sortable: true },
        { name: 'work_group_id', label: 'work_group_id', field: 'work_group_id', align: 'center' },
        { name: 'task_dict_id', label: 'task_dict_id', field: 'task_dict_id', align: 'center' },
        { name: 'error_message', label: 'Ошибка', field: 'error_message', align: 'left', style: 'max-width: 400px; white-space: normal;' },
      ];
    },
    assignedWorkGroupsCount() {
      return this.workGroups.filter(wg => wg.is_assigned_).length;
    },
    blocksForCopyFromDialog() {
      // Все блоки кроме того, который открыт в диалоге
      return this.blocks.filter(b => b.block_id !== this.employeesDialog.block?.block_id);
    },
    filteredEmployeesForAdd() {
      // Исключаем уже добавленных пользователей
      const addedIds = this.blockEmployees.map(e => e.employee_id);
      let filtered = this.allEmployees.filter(e => !addedIds.includes(e.employee_id));
      
      // Поиск по запросу (по имени, email или ID)
      if (this.employeeSearchQuery) {
        const query = this.employeeSearchQuery.toLowerCase();
        filtered = filtered.filter(e => 
          e.fio?.toLowerCase().includes(query) ||
          e.email?.toLowerCase().includes(query) ||
          String(e.employee_id).includes(query)
        );
      }
      
      return filtered;
    },
    isAllLevelsSelected() {
      if (!this.levels.length) return false;
      return this.levels.every(l => this.selectedLevelIds.includes(l.work_group_level_id));
    }
  },
  methods: {
    formatDate,
    
    loadFiltersFromStorage() {
      const savedSearch = localStorage.getItem('tier_blocks_search');
      const savedFilter = localStorage.getItem('tier_blocks_filter_only_with_blocks');
      
      if (savedSearch) {
        this.residentSearchQuery = savedSearch;
      }
      
      if (savedFilter !== null) {
        this.filterOnlyWithBlocks = savedFilter === 'true';
      }
    },
    
    onSearchChange() {
      localStorage.setItem('tier_blocks_search', this.residentSearchQuery);
    },
    
    onFilterChange() {
      localStorage.setItem('tier_blocks_filter_only_with_blocks', this.filterOnlyWithBlocks);
    },
    
    async loadResidents() {
      this.isLoadingResidents = true;
      const res = await getResidents();
      this.residents = res || [];
      this.isLoadingResidents = false;
    },

    async selectResident(resident) {
      this.selectedResident = resident;
      this.selectedBlock = null;
      this.workGroups = [];
      await this.loadBlocks();
    },

    async loadBlocks() {
      if (!this.selectedResident) {
        this.blocks = [];
        return;
      }
      this.isLoadingBlocks = true;
      const res = await getBlocksList({ resident_id: this.selectedResident.resident_id });
      this.blocks = res || [];
      this.isLoadingBlocks = false;
      
      // Автоматически выбираем первый блок, если есть
      if (this.blocks.length > 0) {
        await this.selectBlock(this.blocks[0]);
      }
    },

    async selectBlock(block) {
      this.selectedBlock = block;
      await this.loadWorkGroups();
    },

    async loadWorkGroups() {
      if (!this.selectedBlock) {
        this.workGroups = [];
        return;
      }
      this.isLoadingWorkGroups = true;
      const res = await getWorkGroupsList(this.selectedBlock.block_id);
      this.workGroups = res || [];
      this.isLoadingWorkGroups = false;
    },

    async selectWorkGroup(workGroup) {
      this.selectedWorkGroup = workGroup;
      await this.loadLevels();
    },

    async loadLevels() {
      if (!this.selectedWorkGroup || !this.selectedWorkGroup.work_group_id || !this.selectedWorkGroup.is_level_need) {
        this.levels = [];
        this.selectedLevelIds = [];
        return;
      }
      this.isLoadingLevels = true;
      const res = await getWorkGroupLevels(this.selectedWorkGroup.work_group_id);
      this.levels = res || [];
      this.isLoadingLevels = false;
    },

    async reloadDataSilently() {
      // Тихое обновление без лоадеров
      const prevSelectedResident = this.selectedResident;
      const prevSelectedBlock = this.selectedBlock;
      const prevSelectedWorkGroup = this.selectedWorkGroup;
      
      // Обновляем проекты
      const residentsRes = await getResidents();
      if (residentsRes) {
        this.residents = residentsRes;
      }
      
      // Обновляем блоки если был выбран проект
      if (prevSelectedResident) {
        const blocksRes = await getBlocksList({ resident_id: prevSelectedResident.resident_id });
        if (blocksRes) {
          this.blocks = blocksRes;
          
          // Обновляем выбранный блок
          if (prevSelectedBlock) {
            const updatedBlock = blocksRes.find(b => b.block_id === prevSelectedBlock.block_id);
            if (updatedBlock) {
              this.selectedBlock = updatedBlock;
            }
          }
        }
        
        // Обновляем группы работ если был выбран блок
        if (prevSelectedBlock) {
          const workGroupsRes = await getWorkGroupsList(prevSelectedBlock.block_id);
          if (workGroupsRes) {
            this.workGroups = workGroupsRes;
            
            // Обновляем выбранную группу работ
            if (prevSelectedWorkGroup) {
              const updatedWorkGroup = workGroupsRes.find(wg => wg.work_group_id === prevSelectedWorkGroup.work_group_id);
              if (updatedWorkGroup) {
                this.selectedWorkGroup = updatedWorkGroup;
              }
            }
          }
        }
        
        // Обновляем этажи если была выбрана группа работ и для нее нужны этажи
        if (prevSelectedWorkGroup && prevSelectedWorkGroup.work_group_id && prevSelectedWorkGroup.is_level_need) {
          const levelsRes = await getWorkGroupLevels(prevSelectedWorkGroup.work_group_id);
          if (levelsRes) {
            this.levels = levelsRes;
          }
        }
      }
      // После любого тихого обновления сбрасываем выбор этажей
      this.selectedLevelIds = [];
    },

    onAddBlockClick() {
      this.showModal(MODAL_NAMES.AddBlock, {
        title: 'Создать блок',
        body: {
          resident_id: this.selectedResident.resident_id
        },
        onSubmit: async (body) => {
          await modifyBlock(body);
          this.showSucceedSnackbar('Блок создан');
          this.closeModal();
          await this.reloadDataSilently();
        },
      });
    },

    onEditBlockClick(block) {
      this.showModal(MODAL_NAMES.AddBlock, {
        title: 'Редактировать блок',
        body: block,
        onSubmit: async (body) => {
          await modifyBlock(body);
          this.showSucceedSnackbar('Блок обновлен');
          this.closeModal();
          await this.reloadDataSilently();
        },
      });
    },

    async onDeleteBlockClick(block) {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить блок "${block.block_name}"?`,
        onConfirm: async () => {
          await deleteBlock(block.block_id);
          this.showSucceedSnackbar('Блок удален');
          
          if (this.selectedBlock && this.selectedBlock.block_id === block.block_id) {
            this.selectedBlock = null;
            this.workGroups = [];
          }
          
          await this.reloadDataSilently();
          this.confirmDialog.show = false;
        }
      };
    },

    async onWorkGroupCheckboxChange(isChecked, workGroup) {
      if (isChecked) {
        this.showModal(MODAL_NAMES.AddTierGroupWork, {
          title: `Добавить "${workGroup.work_group_name}"`,
          body: { 
            block_id: this.selectedBlock.block_id,
            work_group_dict_id: workGroup.work_group_dict_id 
          },
          onSubmit: async (body) => {
            await modifyWorkGroup(body);
            this.showSucceedSnackbar('Группа работ добавлена');
            this.closeModal();
            await this.reloadDataSilently();
          },
        });
      } else {
        if (workGroup.work_group_id) {
          // Сначала получаем статистику связей
          try {
            const stats = await getWorkGroupDeleteStats(workGroup.work_group_id);
            
            // Формируем сообщение со статистикой
            let message = `Вы хотите удалить конструктив "${stats?.work_group_name || workGroup.work_group_name}".\n\n`;
            message += `Связанные данные:\n`;
            message += `• Задач: ${stats?.tasks_count || 0}\n`;
            message += `• Планов: ${stats?.plans_count || 0}\n`;
            message += `• Фактов: ${stats?.facts_count || 0}\n`;
            message += `• Этажей: ${stats?.levels_count || 0}\n`;
            message += `• Записей по людям: ${stats?.labor_count || 0}\n\n`;
            message += `ВСЕ эти данные будут удалены. Продолжить?`;
            
            this.confirmDialog = {
              show: true,
              title: 'Удаление конструктива',
              message: message,
              onConfirm: async () => {
                await deleteWorkGroup(workGroup.work_group_id);
                this.showSucceedSnackbar('Конструктив и все связанные данные удалены');
                
                // Сбрасываем выбранную группу работ если она была удалена
                if (this.selectedWorkGroup?.work_group_id === workGroup.work_group_id) {
                  this.selectedWorkGroup = null;
                  this.levels = [];
                }
                
                await this.reloadDataSilently();
                this.confirmDialog.show = false;
              }
            };
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при получении данных';
            this.showErrorSnackbar(errorMessage);
          }
        }
      }
    },

    async onViewTasksClick(workGroup) {
      this.showModal(MODAL_NAMES_AIDAR.ViewWorkGroupTasks, {
        title: `Задачи: ${workGroup.work_group_name}`,
        body: {
          work_group_id: workGroup.work_group_id,
          work_group_dict_id: workGroup.work_group_dict_id,
          work_group_name: workGroup.work_group_name
        },
        onDelete: async () => {
          // Обновляем данные после удаления задачи
          await this.reloadDataSilently();
        }
      });
    },

    onEditWorkGroupClick(workGroup) {
      const { date_start, date_end, ...rest } = workGroup;
      this.showModal(MODAL_NAMES.AddTierGroupWork, {
        title: 'Редактировать группу работ',
        body: {
          ...rest,
          date_start: date_start ? this.getLocaleDate(date_start) : '',
          date_end: date_end ? this.getLocaleDate(date_end) : '',
        },
        onSubmit: async (body) => {
          await modifyWorkGroup(body);
          this.showSucceedSnackbar('Группа работ обновлена');
          this.closeModal();
          await this.reloadDataSilently();
        },
      });
    },

    onAddLevelClick() {
      this.showModal(MODAL_NAMES_AIDAR.AddWorkGroupLevel, {
        title: 'Добавить этажи',
        body: {
          work_group_id: this.selectedWorkGroup.work_group_id,
          work_group_date_start: this.selectedWorkGroup.date_start,
          work_group_date_end: this.selectedWorkGroup.date_end
        },
        onSubmitManual: async (body) => {
          try {
            await modifyWorkGroupLevel(body);
            this.showSucceedSnackbar('Этаж добавлен');
            this.closeModal();
            await this.reloadDataSilently();
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при добавлении этажа';
            this.showErrorSnackbar(errorMessage);
          }
        },
        onSubmitAuto: async (body) => {
          try {
            await bulkAddWorkGroupLevels(body);
            this.showSucceedSnackbar('Этажи добавлены');
            this.closeModal();
            await this.reloadDataSilently();
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при добавлении этажей';
            this.showErrorSnackbar(errorMessage);
          }
        },
      });
    },

    onEditLevelClick(level) {
      const { level_date_start, level_date_end, ...rest } = level;
      this.showModal(MODAL_NAMES_AIDAR.EditWorkGroupLevel, {
        title: 'Редактировать этаж',
        body: {
          ...rest,
          level_date_start: level_date_start ? this.getLocaleDate(level_date_start) : '',
          level_date_end: level_date_end ? this.getLocaleDate(level_date_end) : '',
          work_group_date_start: this.selectedWorkGroup.date_start,
          work_group_date_end: this.selectedWorkGroup.date_end
        },
        onSubmit: async (body) => {
          try {
            await modifyWorkGroupLevel(body);
            this.showSucceedSnackbar('Этаж обновлен');
            this.closeModal();
            await this.reloadDataSilently();
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при обновлении этажа';
            if (errorMessage && (errorMessage.includes('план-факт') || errorMessage.includes('Подтвердите пересчёт'))) {
              this.confirmDialog = {
                show: true,
                title: 'Подтверждение пересчёта',
                message: 'На этажах есть план-факт. При пересчёте дат план и факт будут полностью удалены. Продолжить?',
                onConfirm: async () => {
                  try {
                    await modifyWorkGroupLevel({ ...body, clear_plan_fact: true });
                    this.showSucceedSnackbar('Этаж обновлен');
                    this.closeModal();
                    this.confirmDialog.show = false;
                    await this.reloadDataSilently();
                  } catch (e2) {
                    const msg2 = e2?.response?.data?.message || e2?.message || 'Ошибка при обновлении этажа';
                    this.showErrorSnackbar(msg2);
                    this.confirmDialog.show = false;
                  }
                }
              };
            } else {
              this.showErrorSnackbar(errorMessage);
            }
          }
        },
      });
    },

    async onDeleteLevelClick(level) {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить этаж "${level.level_num}"?`,
        onConfirm: async () => {
          await deleteWorkGroupLevel(level.work_group_level_id);
          this.showSucceedSnackbar('Этаж удален');
          await this.reloadDataSilently();
          this.confirmDialog.show = false;
        }
      };
    },

    onLevelCheckboxChange(isChecked, level) {
      const id = level.work_group_level_id;
      if (!id) return;
      if (isChecked) {
        if (!this.selectedLevelIds.includes(id)) {
          this.selectedLevelIds.push(id);
        }
      } else {
        this.selectedLevelIds = this.selectedLevelIds.filter(x => x !== id);
      }
    },

    toggleSelectAllLevels() {
      if (!this.levels.length) return;
      if (this.isAllLevelsSelected) {
        this.selectedLevelIds = [];
      } else {
        this.selectedLevelIds = this.levels
          .map(l => l.work_group_level_id)
          .filter(id => id != null);
      }
    },

    async onBulkDeleteLevelsClick() {
      if (!this.selectedLevelIds.length) return;

      const count = this.selectedLevelIds.length;
      const confirmed = confirm(`Удалить ${count} выбранных этажей?`);
      if (!confirmed) return;

      try {
        for (const id of this.selectedLevelIds) {
          await deleteWorkGroupLevel(id);
        }
        this.showSucceedSnackbar(`Удалено этажей: ${count}`);
        this.selectedLevelIds = [];
        await this.reloadDataSilently();
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при массовом удалении этажей';
        this.showErrorSnackbar(errorMessage);
      }
    },

    getLocaleDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    },

    // Извлекаем имя из формата "123 / Имя" -> "Имя"
    getEmployeeName(fio) {
      if (!fio) return '';
      const parts = fio.split(' / ');
      return parts.length > 1 ? parts.slice(1).join(' / ') : fio;
    },

    // Извлекаем первую букву имени
    getEmployeeInitial(fio) {
      const name = this.getEmployeeName(fio);
      return name?.charAt(0)?.toUpperCase() || '?';
    },

    async onConfirmDelete() {
      if (this.confirmDialog.onConfirm) {
        await this.confirmDialog.onConfirm();
      }
    },

    // ============ ПОЛЬЗОВАТЕЛИ БЛОКА ============
    
    async onBlockEmployeesClick(block) {
      this.employeesDialog.block = block;
      this.employeesDialog.show = true;
      await this.loadBlockEmployees(block.block_id);
    },

    async loadBlockEmployees(blockId) {
      if (!blockId) {
        this.blockEmployees = [];
        return;
      }
      this.isLoadingBlockEmployees = true;
      const res = await getBlockEmployees(blockId);
      this.blockEmployees = res || [];
      this.isLoadingBlockEmployees = false;
    },

    async loadAllEmployees() {
      if (this.allEmployees.length > 0) return; // Уже загружены
      
      this.isLoadingAllEmployees = true;
      const res = await getEmployees();
      this.allEmployees = res || [];
      this.isLoadingAllEmployees = false;
    },

    async onAddBlockEmployeeClick() {
      await this.loadAllEmployees();
      this.addEmployeeDialog.selectedEmployeeId = null;
      this.employeeSearchQuery = '';
      this.addEmployeeDialog.show = true;
    },

    filterEmployees(val, update) {
      this.employeeSearchQuery = val;
      update();
    },

    async onConfirmAddEmployee() {
      if (!this.addEmployeeDialog.selectedEmployeeId || !this.employeesDialog.block) return;
      
      try {
        await addBlockEmployee({
          block_id: this.employeesDialog.block.block_id,
          employee_id: this.addEmployeeDialog.selectedEmployeeId
        });
        this.showSucceedSnackbar('Пользователь добавлен');
        this.addEmployeeDialog.show = false;
        await this.loadBlockEmployees(this.employeesDialog.block.block_id);
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при добавлении пользователя';
        this.showErrorSnackbar(errorMessage);
      }
    },

    async onDeleteBlockEmployeeClick(emp) {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Удалить пользователя "${emp.employee_fio}" из блока?`,
        onConfirm: async () => {
          await deleteBlockEmployee(emp.block_employee_id);
          this.showSucceedSnackbar('Пользователь удален');
          await this.loadBlockEmployees(this.employeesDialog.block.block_id);
          this.confirmDialog.show = false;
        }
      };
    },

    onCopyEmployeesClick() {
      this.copyDialog.selectedBlocks = [];
      this.copyDialog.show = true;
    },

    async onConfirmCopyEmployees() {
      if (this.copyDialog.selectedBlocks.length === 0 || !this.employeesDialog.block) return;
      
      try {
        const result = await copyBlockEmployees({
          source_block_id: this.employeesDialog.block.block_id,
          target_block_ids: this.copyDialog.selectedBlocks
        });
        // Извлекаем число из разных форматов ответа
        let count = 0;
        if (typeof result === 'number') {
          count = result;
        } else if (Array.isArray(result) && result.length > 0) {
          const firstItem = result[0];
          count = Object.values(firstItem)[0] || 0;
        } else if (result && typeof result === 'object') {
          count = result.data || result.count || Object.values(result)[0] || 0;
        }
        this.showSucceedSnackbar(`Скопировано ${count} записей`);
        this.copyDialog.show = false;
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при копировании';
        this.showErrorSnackbar(errorMessage);
      }
    },

    // ============ АДМИН ОПЕРАЦИИ ============
    
    onAdminClick() {
      this.pinDialog.pin = '';
      this.pinDialog.error = false;
      this.pinDialog.show = true;
    },
    
    onPinInput(val) {
      this.pinDialog.error = false;
      if (val === '1999') {
        this.pinDialog.show = false;
        this.adminDialog.show = true;
      } else if (val.length === 4) {
        this.pinDialog.error = true;
      }
    },
    
    async onFullRecalcClick() {
      if (this.isRecalculating) return;
      
      if (!confirm('Выполнить полный пересчёт долгов и дней отставания по ВСЕМ проектам?\n\nЭто может занять несколько минут.')) {
        return;
      }
      
      this.isRecalculating = true;
      try {
        const result = await fullRecalcAll();
        // Извлекаем данные из результата
        let totalFacts = 0;
        let totalGroups = 0;
        if (Array.isArray(result) && result.length > 0) {
          totalFacts = result[0].total_facts || 0;
          totalGroups = result[0].total_groups || 0;
        }
        this.showSucceedSnackbar(`Пересчёт завершён! Задач: ${totalFacts}, групп работ: ${totalGroups}`);
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при пересчёте';
        this.showErrorSnackbar(errorMessage);
      } finally {
        this.isRecalculating = false;
      }
    },
    
    async onViewAnomaliesClick() {
      this.anomaliesData = [];
      this.anomaliesDialog.show = true;
      this.isLoadingAnomalies = true;
      try {
        const result = await getPlanFactAnomalies();
        this.anomaliesData = result || [];
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при загрузке аномалий';
        this.showErrorSnackbar(errorMessage);
      } finally {
        this.isLoadingAnomalies = false;
      }
    },
  },
  
  async mounted() {
    this.loadFiltersFromStorage();
    await this.loadResidents();
  },
  
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    const { showModal, closeModal } = useAppStore();
    return { showSucceedSnackbar, showErrorSnackbar, showModal, closeModal };
  },
};
</script>

<style scoped>
.tier-blocks {
  padding: 8px;
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tier-blocks__header {
  background: white;
  border: 1px solid #ddd;
  padding: 6px 8px;
  box-shadow: none;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-label {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
}

.simple-input {
  min-width: 250px;
  padding: 4px 8px;
  font-size: 12px;
  border: 1px solid #ccc;
  border-radius: 3px;
  background: white;
  color: #333;
  outline: none;
  height: 28px;
}

.simple-input:hover {
  border-color: #1976d2;
}

.simple-input:focus {
  border-color: #1976d2;
  box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.2);
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
}

.filter-label:hover {
  color: #1976d2;
}

.filter-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.blocks-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 6px;
  padding: 6px 8px;
  background: #f9f9f9;
  border-bottom: 1px solid #ddd;
  flex-shrink: 0;
  max-height: 120px;
  overflow-y: auto;
}

.add-block-btn-inline {
  flex-shrink: 0;
}

.blocks-wrap-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
}

.block-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  flex-shrink: 0;
}

.block-item:hover {
  background: #f5f5f5;
  border-color: #1976d2;
}

.block-item--active {
  background: #e3f2fd;
  border-color: #1976d2;
  font-weight: 500;
}

.block-item__name {
  font-size: 12px;
  color: #333;
}

.block-item--active .block-item__name {
  color: #1976d2;
}

.block-item__icon {
  flex-shrink: 0;
}

.block-item__actions {
  display: flex;
  gap: 2px;
  margin-left: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.block-item:hover .block-item__actions {
  opacity: 1;
}

.tier-blocks__content {
  flex: 1;
  display: grid;
  grid-template-columns: 300px 450px 1fr;
  gap: 6px;
  overflow: hidden;
}

.tier-blocks__column {
  background: white;
  border: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tier-blocks__column--with-blocks {
  display: flex;
  flex-direction: column;
}

.column-header {
  padding: 6px 8px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}

.column-title {
  flex: 1;
}

.column-list-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.column-list {
  padding: 0;
}

.virtual-scroll-list {
  height: 100%;
  max-height: 100%;
}

.virtual-scroll-list .q-virtual-scroll__content {
  padding: 0;
}

.list-item {
  border-bottom: 1px solid #f0f0f0;
  min-height: 36px;
  padding: 4px 8px;
}

.list-item:hover {
  background-color: #f5f5f5;
}

.list-item.q-item--active {
  background-color: #e3f2fd;
}

.item-label {
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
}

.work-group-name {
  font-size: 13px;
  font-weight: 500;
  color: #222;
}

.item-caption {
  font-size: 10px;
  color: #666;
  margin-top: 2px;
  display: flex;
  align-items: center;
}

.work-group-dates {
  font-size: 11px;
  color: #1976d2;
  font-weight: 500;
  margin-top: 4px;
}

.work-group-employee {
  font-size: 11px;
  color: #2e7d32;
  font-weight: 500;
  margin-top: 3px;
}

.item-actions-section {
  display: flex;
  align-items: center;
  gap: 2px;
}

.work-group-item {
  cursor: default;
}

.clickable-section {
  cursor: pointer;
  transition: background-color 0.2s;
}

.clickable-section:hover {
  background-color: #f5f5f5;
}

.active-section {
  background-color: #e3f2fd !important;
  border-radius: 4px;
}

.level-item {
  background: white;
}

.floor-order-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 6px;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.work-group-item:hover {
  background-color: #f5f5f5;
}

.info-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  color: #555;
}

.info-message {
  font-size: 13px;
  text-align: center;
  color: #1976d2;
  margin: 0;
  max-width: 300px;
  line-height: 1.4;
}

.empty-state,
.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #999;
  font-size: 12px;
}

.empty-state p,
.loading-state p {
  margin: 0;
}

@media (max-width: 1200px) {
  .tier-blocks__content {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto 1fr;
  }
  
  .tier-blocks__column {
    max-height: 300px;
  }
}

/* Debug ID styles */
.debug-id-visible {
  font-size: 11px !important;
  color: #e65100 !important;
  font-family: 'Consolas', 'Monaco', monospace !important;
  font-weight: 600 !important;
  background: #fff3e0;
  padding: 1px 4px;
  border-radius: 2px;
  margin-top: 2px;
}

.debug-id-visible-inline {
  font-size: 10px;
  color: #e65100;
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 600;
  background: #fff3e0;
  padding: 1px 4px;
  border-radius: 2px;
  margin-left: 6px;
}

.header-separator {
  width: 1px;
  height: 20px;
  background: #ddd;
  margin: 0 8px;
}

.filter-label--debug {
  color: #e65100;
  gap: 4px;
}

.filter-label--debug:hover {
  color: #bf360c;
}


.employee-item {
  border-bottom: 1px solid #eee;
}

.employee-item:last-child {
  border-bottom: none;
}

.anomalies-table {
  max-height: calc(100vh - 200px);
}

.anomalies-table .q-table__top,
.anomalies-table .q-table__bottom,
.anomalies-table thead tr:first-child th {
  background-color: #f5f5f5;
}

.anomalies-table thead tr th {
  position: sticky;
  z-index: 1;
  top: 0;
}

.levels-tabs {
  flex-shrink: 0;
  background: #fafafa;
  border-bottom: 1px solid #ddd;
}

.levels-tabs :deep(.q-tab) {
  min-height: 32px;
  padding: 0 12px;
  font-size: 11px;
}

.levels-tab-panels {
  flex: 1;
  overflow: hidden;
}

.levels-tab-panels :deep(.q-tab-panel) {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Цвета для группировки этажей */
.level-detail-item {
  border-left: 3px solid transparent;
}

.level-detail-label {
  display: flex;
  align-items: center;
}

.level-color-1 {
  background: #e3f2fd;
  border-left-color: #1976d2;
}

.level-color-2 {
  background: #fff3e0;
  border-left-color: #f57c00;
}

.level-color-3 {
  background: #e8f5e9;
  border-left-color: #388e3c;
}

.level-color-4 {
  background: #fce4ec;
  border-left-color: #c2185b;
}

.level-color-5 {
  background: #f3e5f5;
  border-left-color: #7b1fa2;
}

.level-color-6 {
  background: #e0f7fa;
  border-left-color: #0097a7;
}

/* Таблица level details */
.level-details-table-container {
  flex: 1;
  overflow: auto;
}

.level-details-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.level-details-table th {
  position: sticky;
  top: 0;
  background: #f5f5f5;
  padding: 6px 8px;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
}

.level-details-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #eee;
}

.level-details-table tr {
  border-left: 3px solid transparent;
}

.level-details-table .col-level {
  font-weight: 500;
  white-space: nowrap;
}

.level-details-table .col-date {
  white-space: nowrap;
  color: #666;
}

.level-details-table .col-date.date-changed {
  color: #e65100;
  font-weight: 600;
}

.level-details-table .col-original-date {
  color: #999;
  font-size: 10px;
}

.level-details-table .col-days {
  text-align: center;
  font-weight: 600;
  white-space: nowrap;
}

.level-details-table .col-id {
  font-family: monospace;
  font-size: 10px;
  color: #999;
}

.day-cnt-negative {
  color: #c62828;
  background: rgba(198, 40, 40, 0.08);
}

.day-cnt-positive {
  color: #2e7d32;
  background: rgba(46, 125, 50, 0.08);
}
</style>
